import type {
    TimelineFishGroup,
    TimelineOperation,
    TimelineStay,
    TimelineWeight,
} from '@/domain/fishGroups';

import type {
    BadgeKind,
    BarSegment,
    EndedKind,
    OperationBadgeKind,
    PenTag,
    TrackBadge,
    TrackMarker,
    TrackTick,
    WeekAxis,
} from '../types';

import { layoutTrackLabels, TRACK_MIN_WIDTH_PX } from './trackLabels';
import { groupEnd, isOnAxis, isPlannedStay, isSameDay, positionOf } from './weekAxis';

const MARKER_GAP_MS = 3 * 24 * 60 * 60 * 1000;
/**
 * Coverage gaps shorter than this are bookkeeping, not a pen without a bioscope: a bioscope
 * move closes the old placement a second before the move (two placements of one bioscope may
 * not touch), so a stay that ends with the move would end on a sliver of "No Bioscope", drawn
 * at the minimum width. Far below anything a week axis can show.
 */
const COVERAGE_GAP_TOLERANCE_MS = 60 * 60 * 1000;
/** The legend's order, which a same-day badge keeps whichever was recorded first. */
export const BADGE_KIND_ORDER: readonly BadgeKind[] = [
    'delousing',
    'move',
    'split',
    'grading',
    'redistribution',
    'merge',
    'harvest',
];

/** The badge an operation draws, or null for one the board doesn't mark (e.g. a plain close). */
export function badgeForOperation(operation: TimelineOperation | null): OperationBadgeKind | null {
    if (!operation) return null;
    switch (operation.type) {
        case 'split':
            return operation.reason?.trim().toLowerCase() === 'grading' ? 'grading' : 'split';
        // Several sources into several destinations on one day: a top-up, not a split, unless
        // it was recorded as a grading.
        case 'redistribution':
            return operation.reason?.trim().toLowerCase() === 'grading'
                ? 'grading'
                : 'redistribution';
        case 'merge':
        case 'split_and_merge':
            return 'merge';
        case 'closure':
            return operation.reason === 'harvested' ? 'harvest' : null;
        default:
            return null;
    }
}

export function endedKindOf(group: TimelineFishGroup): EndedKind | null {
    if (group.isLive) return null;
    const fromOperation = badgeForOperation(group.endedBy);
    if (fromOperation) return fromOperation;
    // Groups ended through the legacy flow carry no operation, only their status.
    if (group.status === 'harvested') return 'harvest';
    if (group.status === 'merged') return 'merge';
    if (group.status === 'split') return 'split';
    return 'closed';
}

function stayEnd(stay: TimelineStay, group: TimelineFishGroup, now: Date): Date {
    return stay.to ?? groupEnd(group, now);
}

/**
 * One segment per stretch of the stay with or without a bioscope in the pen, so a pen that
 * got its bioscope weeks after stocking reads as hatched, then solid.
 */
function staySegments(
    stay: TimelineStay,
    end: Date,
    axis: WeekAxis,
    now: Date
): Omit<BarSegment, 'revealsPen'>[] {
    if (isPlannedStay(stay, now)) {
        const left = positionOf(axis, stay.from);
        return [
            {
                key: `${stay.id}-planned`,
                leftPct: left,
                widthPct: Math.max(positionOf(axis, end) - left, 0),
                noBioscope: false,
                planned: true,
                penName: stay.cageName,
                stayKey: String(stay.id),
                weight: null,
            },
        ];
    }
    const covered = stay.bioscopes
        .map((coverage) => ({
            from: Math.max(coverage.from.getTime(), stay.from.getTime()),
            to: Math.min(coverage.to?.getTime() ?? end.getTime(), end.getTime()),
        }))
        .filter((range) => range.to > range.from)
        .sort((a, b) => a.from - b.from);

    const ranges: CoverageRange[] = [];
    let cursor = stay.from.getTime();
    for (const range of covered) {
        if (range.from > cursor) ranges.push({ from: cursor, to: range.from, noBioscope: true });
        const from = Math.max(cursor, range.from);
        if (range.to > from) ranges.push({ from, to: range.to, noBioscope: false });
        cursor = Math.max(cursor, range.to);
    }
    if (end.getTime() > cursor) ranges.push({ from: cursor, to: end.getTime(), noBioscope: true });

    return bridgeShortGaps(ranges, covered.length > 0).map((range) => {
        const left = positionOf(axis, new Date(range.from));
        return {
            key: `${stay.id}-${range.from}`,
            leftPct: left,
            widthPct: Math.max(positionOf(axis, new Date(range.to)) - left, 0),
            noBioscope: range.noBioscope,
            planned: false,
            penName: stay.cageName,
            stayKey: String(stay.id),
            weight: stay.weight,
        };
    });
}

interface CoverageRange {
    from: number;
    to: number;
    noBioscope: boolean;
}

/**
 * Folds an uncovered stretch shorter than the tolerance into its covered neighbours. A stay
 * the bioscopes never covered keeps its "No Bioscope", however short.
 */
function bridgeShortGaps(ranges: CoverageRange[], hasCoverage: boolean): CoverageRange[] {
    if (!hasCoverage) return ranges;
    const merged: CoverageRange[] = [];
    for (const range of ranges) {
        const noBioscope = range.noBioscope && range.to - range.from >= COVERAGE_GAP_TOLERANCE_MS;
        const last = merged.at(-1);
        if (last && last.noBioscope === noBioscope && last.to === range.from) {
            last.to = range.to;
        } else {
            merged.push({ ...range, noBioscope });
        }
    }
    return merged;
}

interface BadgeItem {
    date: Date;
    kind: BadgeKind;
    peers?: string[];
}

function mergeSameDayBadges(items: BadgeItem[], axis: WeekAxis) {
    const sorted = [...items].sort((a, b) => a.date.getTime() - b.date.getTime());
    const badges: (TrackBadge & { date: Date })[] = [];
    for (const item of sorted) {
        const previous = badges[badges.length - 1];
        if (previous && isSameDay(previous.date, item.date)) {
            if (!previous.kinds.includes(item.kind)) previous.kinds.push(item.kind);
            previous.peers = [...new Set([...previous.peers, ...(item.peers ?? [])])];
        } else {
            badges.push({
                key: `${item.kind}-${item.date.getTime()}`,
                leftPct: positionOf(axis, item.date),
                kinds: [item.kind],
                labelShown: true,
                peers: item.peers ?? [],
                date: item.date,
            });
        }
    }
    return badges.map(({ date: _date, kinds, ...badge }) => ({
        ...badge,
        kinds: [...kinds].sort((a, b) => BADGE_KIND_ORDER.indexOf(a) - BADGE_KIND_ORDER.indexOf(b)),
    }));
}

export interface GroupTrack {
    segments: BarSegment[];
    penTags: PenTag[];
    badges: TrackBadge[];
    markers: TrackMarker[];
    ticks: TrackTick[];
    endedKind: EndedKind | null;
    weight: TimelineWeight;
    currentPen: string | null;
    endPct: number;
    bioscopeIds: number[];
}

/** Everything drawn on one group's row, from its stays and events at this site. */
export function buildGroupTrack(
    group: TimelineFishGroup,
    axis: WeekAxis,
    now: Date,
    hasParents: boolean,
    /** The other groups in an operation, by display name; only a redistribution lists them. */
    operationPeers: (operationId: number) => string[] = () => []
): GroupTrack {
    const penTags: PenTag[] = group.stays.map((stay, index) => ({
        key: String(stay.id),
        leftPct: positionOf(axis, stay.from),
        penName: stay.cageName,
        afterMove: index > 0,
        hidden: false,
    }));
    const segments = group.stays.flatMap((stay) =>
        staySegments(stay, stayEnd(stay, group, now), axis, now).map((segment) => ({
            ...segment,
            revealsPen: false,
        }))
    );

    const badgeItems: BadgeItem[] = [];
    const markers: TrackMarker[] = [];
    const ticks: TrackTick[] = [];
    const addMarker = (kind: TrackMarker['kind'], date: Date) => {
        if (isOnAxis(axis, date)) {
            markers.push({
                key: `${kind}-${date.getTime()}`,
                leftPct: positionOf(axis, date),
                kind,
                labelKinds: [kind],
            });
        }
    };

    for (const event of group.events) {
        if (event.type === 'delousing') badgeItems.push({ date: event.date, kind: 'delousing' });
        if (event.type === 'harvest') {
            ticks.push({ key: event.id, leftPct: positionOf(axis, event.date) });
        }
        if (event.type !== 'relocated') continue;
        if (event.movedOffSite) addMarker('toOffSite', event.date);
        else if (event.movedFromOffSite) addMarker('fromOffSite', event.date);
        else badgeItems.push({ date: event.date, kind: 'move' });
    }

    const firstStay = group.stays[0];
    const lastStay = group.stays[group.stays.length - 1];
    const arrivedFromOffSite = group.events.some((event) => event.movedFromOffSite);
    if (group.offSiteLinks.includes('fromOffSite') && !arrivedFromOffSite && firstStay) {
        addMarker('fromOffSite', firstStay.from);
    }
    const leftForOffSite = group.events.some((event) => event.movedOffSite);
    if (group.offSiteLinks.includes('toOffSite') && !leftForOffSite && lastStay) {
        addMarker('toOffSite', stayEnd(lastStay, group, now));
    }
    if (!hasParents && !group.offSiteLinks.includes('fromOffSite') && firstStay) {
        addMarker('stocking', firstStay.from);
    }
    // Only a bioscope that arrived after the fish is worth a marker; one measuring from the
    // first days would sit on top of the stocking marker and say nothing new.
    const firstMeasurement = group.firstMeasurementDate;
    if (
        firstMeasurement &&
        (!firstStay || firstMeasurement.getTime() - firstStay.from.getTime() > MARKER_GAP_MS)
    ) {
        addMarker('firstMeasurement', firstMeasurement);
    }

    const endBadge = badgeForOperation(group.endedBy);
    if (endBadge && !group.isLive) {
        const peers =
            endBadge === 'redistribution' && group.endedBy ? operationPeers(group.endedBy.id) : [];
        badgeItems.push({ date: groupEnd(group, now), kind: endBadge, peers });
    }

    const openStay = lastStay && lastStay.to === null && group.isLive ? lastStay : null;
    const coverageStay = openStay ?? lastStay;
    const bioscopeIds = [
        ...new Set(
            (coverageStay?.bioscopes ?? [])
                .filter(
                    (coverage) =>
                        !openStay ||
                        (coverage.from <= now && (coverage.to === null || coverage.to > now))
                )
                .map((coverage) => coverage.bioscopeId)
        ),
    ];

    // Laid out for the narrowest track; the board lays the labels out again at its width.
    return layoutTrackLabels(
        {
            segments,
            penTags,
            badges: mergeSameDayBadges(badgeItems, axis),
            markers,
            ticks,
            endedKind: endedKindOf(group),
            weight: group.weight,
            currentPen: openStay?.cageName ?? null,
            endPct: positionOf(axis, groupEnd(group, now)),
            bioscopeIds,
        },
        TRACK_MIN_WIDTH_PX
    );
}
